// frontend/src/utils/qaMode.js
// QAモード: ?qa=1 でアクセスすると、画面上に問題箇所をオーバーレイ表示

/**
 * QAモードが有効かチェック
 */
export const isQAModeEnabled = () => {
  try {
    // 本番環境では常に無効化
    const isProduction =
      process.env.NODE_ENV === 'production' ||
      String(process.env.REACT_APP_ENV).toLowerCase() === 'production';
    if (isProduction) return false;
    
    const params = new URLSearchParams(window.location.search);
    return params.get('qa') === '1' || localStorage.getItem('medichat_qa_mode') === '1';
  } catch {
    return false;
  }
};

/**
 * QAモードを有効化/無効化
 */
export const toggleQAMode = (enabled) => {
  try {
    if (enabled) {
      localStorage.setItem('medichat_qa_mode', '1');
    } else {
      localStorage.removeItem('medichat_qa_mode');
    }
    window.location.reload();
  } catch (e) {
    console.error('[QA Mode] Toggle failed:', e);
  }
};

/**
 * タップ領域チェック（44x44px未満を検出）
 */
export const checkTapTargets = () => {
  const MIN_SIZE = 44; // px
  const issues = [];
  
  const interactiveElements = document.querySelectorAll(
    'button, a, input, select, textarea, [role="button"], [onclick], [tabindex]'
  );
  
  interactiveElements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(el);
    
    // 非表示要素はスキップ
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') return;
    
    const width = rect.width;
    const height = rect.height;
    
    if (width < MIN_SIZE || height < MIN_SIZE) {
      issues.push({
        type: 'tap_target',
        element: el,
        message: `タップ領域が小さい: ${Math.round(width)}x${Math.round(height)}px (最小44x44px)`,
        severity: 'error',
        rect,
        details: {
          width: Math.round(width),
          height: Math.round(height),
          minSize: MIN_SIZE
        }
      });
    }
  });
  
  return issues;
};

/**
 * コントラスト比チェック（WCAG AA基準: 4.5:1）
 */
export const checkContrast = () => {
  const MIN_CONTRAST = 4.5;
  const issues = [];
  
  // テキスト要素を取得
  const textElements = document.querySelectorAll('p, span, div, label, button, a, input, textarea, h1, h2, h3, h4, h5, h6');
  
  textElements.forEach((el) => {
    const computedStyle = window.getComputedStyle(el);
    
    // 非表示要素はスキップ
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') return;
    // disabled要素は視覚的コントラスト警告の対象外（仕様的に薄くても可）
    if (el.matches(':disabled,[aria-disabled="true"]')) return;
    // 明示的に無視したい要素
    if (el.classList.contains('qa-ignore-contrast')) return;
    
    // テキストがない要素はスキップ
    const text = el.textContent?.trim();
    if (!text || text.length === 0) return;
    
    const color = computedStyle.color;
    const bgColor = computedStyle.backgroundColor;
    const bgImage = computedStyle.backgroundImage;
    
    // 背景がグラデーションの場合は概算色で評価（誤検出を抑制）
    if (bgImage && bgImage.includes('gradient')) {
      // 濃色寄りの概算値（実装側では十分なコントラストを確保しているため）
      const contrast = calculateContrast(color, 'rgb(79, 124, 255)');
      if (contrast < MIN_CONTRAST) {
        issues.push({
          type: 'contrast',
          element: el,
          message: `コントラスト不足: ${contrast.toFixed(2)}:1 (最小${MIN_CONTRAST}:1)`,
          severity: 'warning',
          details: {
            contrast: contrast.toFixed(2),
            minContrast: MIN_CONTRAST,
            color,
            background: 'gradient(approx)'
          }
        });
      }
      return;
    }
    
    // 背景色が透明の場合は親要素の背景色を取得
    let background = bgColor;
    if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
      let parent = el.parentElement;
      while (parent && (window.getComputedStyle(parent).backgroundColor === 'rgba(0, 0, 0, 0)' || 
             window.getComputedStyle(parent).backgroundColor === 'transparent')) {
        // 親がグラデーション背景なら概算値で評価
        const pStyle = window.getComputedStyle(parent);
        if (pStyle.backgroundImage && pStyle.backgroundImage.includes('gradient')) {
          background = 'rgb(79, 124, 255)';
          break;
        }
        parent = parent.parentElement;
      }
      background = parent ? window.getComputedStyle(parent).backgroundColor : 'rgb(255, 255, 255)';
    }
    
    const contrast = calculateContrast(color, background);
    
    if (contrast < MIN_CONTRAST) {
      issues.push({
        type: 'contrast',
        element: el,
        message: `コントラスト不足: ${contrast.toFixed(2)}:1 (最小${MIN_CONTRAST}:1)`,
        severity: 'warning',
        details: {
          contrast: contrast.toFixed(2),
          minContrast: MIN_CONTRAST,
          color,
          background
        }
      });
    }
  });
  
  return issues;
};

/**
 * コントラスト比を計算
 */
const calculateContrast = (color1, color2) => {
  const rgb1 = parseRGB(color1);
  const rgb2 = parseRGB(color2);
  
  if (!rgb1 || !rgb2) return 21; // パースできない場合は問題なしとみなす
  
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
};

const parseRGB = (colorString) => {
  const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return null;
  
  return {
    r: parseInt(match[1]),
    g: parseInt(match[2]),
    b: parseInt(match[3])
  };
};

const relativeLuminance = ({ r, g, b }) => {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;
  
  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);
  
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * 必須フィールドの表示チェック
 */
export const checkRequiredFields = () => {
  const issues = [];
  
  // 必須フィールドを探す
  const requiredInputs = document.querySelectorAll('input[required], textarea[required], select[required]');
  
  requiredInputs.forEach((el) => {
    const label = el.closest('.mc-form-field, .basic-info-field')?.querySelector('label');
    
    if (label) {
      const labelText = label.textContent || '';
      const hasRequiredIndicator = labelText.includes('必須') || 
                                    labelText.includes('※') ||
                                    label.classList.contains('required') ||
                                    label.querySelector('.required, .mc-form-label.required');
      
      if (!hasRequiredIndicator) {
        issues.push({
          type: 'required_field',
          element: el,
          message: '必須フィールドに「※必須」表示がありません',
          severity: 'error',
          details: {
            fieldName: el.name || el.id || 'unknown',
            labelText
          }
        });
      }
    }
  });
  
  return issues;
};

/**
 * フォーカス可能要素のチェック
 */
export const checkFocusable = () => {
  const issues = [];
  
  // スマホ（coarse pointer）ではフォーカスリング警告をデフォルト抑制（誤検出・ノイズ低減）
  try {
    if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      return issues;
    }
  } catch {}
  
  const interactiveElements = document.querySelectorAll(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="button"]:not([disabled])'
  );
  
  interactiveElements.forEach((el) => {
    const computedStyle = window.getComputedStyle(el);
    
    // 非表示要素はスキップ
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') return;
    
    // フォーカスリングが見えるかチェック
    const outlineWidth = computedStyle.outlineWidth;
    const outlineStyle = computedStyle.outlineStyle;
    const outlineColor = computedStyle.outlineColor;
    
    // outline: none の場合は警告
    if (outlineStyle === 'none' && !el.classList.contains('no-outline-ok')) {
      issues.push({
        type: 'focus',
        element: el,
        message: 'フォーカスリングが無効（outline: none）',
        severity: 'warning',
        details: {
          outlineWidth,
          outlineStyle,
          outlineColor
        }
      });
    }
  });
  
  return issues;
};

/**
 * すべてのチェックを実行
 */
export const runAllChecks = () => {
  const allIssues = [
    ...checkTapTargets(),
    ...checkContrast(),
    ...checkRequiredFields(),
    ...checkFocusable()
  ];
  
  return {
    total: allIssues.length,
    errors: allIssues.filter(i => i.severity === 'error'),
    warnings: allIssues.filter(i => i.severity === 'warning'),
    issues: allIssues
  };
};

/**
 * 問題箇所にオーバーレイを表示
 */
export const showQAOverlay = (issues) => {
  // 既存のオーバーレイを削除
  document.querySelectorAll('.qa-overlay-marker').forEach(el => el.remove());
  
  issues.forEach((issue, index) => {
    const rect = issue.element.getBoundingClientRect();
    
    const marker = document.createElement('div');
    marker.className = 'qa-overlay-marker';
    marker.style.cssText = `
      position: fixed;
      top: ${rect.top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border: 3px solid ${issue.severity === 'error' ? '#ef4444' : '#f59e0b'};
      background: ${issue.severity === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
      pointer-events: none;
      z-index: 999999;
      border-radius: 4px;
    `;
    
    const label = document.createElement('div');
    label.style.cssText = `
      position: absolute;
      top: -24px;
      left: 0;
      background: ${issue.severity === 'error' ? '#ef4444' : '#f59e0b'};
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    label.textContent = `${index + 1}. ${issue.type}`;
    
    marker.appendChild(label);
    document.body.appendChild(marker);
    
    // クリックでコンソールに詳細を表示
    marker.addEventListener('click', () => {
      console.log(`[QA Issue #${index + 1}]`, issue);
    });
  });
  
  // サマリーパネルを表示
  showQASummaryPanel(issues);
};

/**
 * QAサマリーパネルを表示
 */
const showQASummaryPanel = (issues) => {
  // 既存のパネルを削除
  document.querySelectorAll('.qa-summary-panel').forEach(el => el.remove());
  
  const panel = document.createElement('div');
  panel.className = 'qa-summary-panel';
  panel.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.2);
    z-index: 1000000;
    font-family: system-ui, -apple-system, sans-serif;
    max-width: 300px;
  `;
  
  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');
  
  panel.innerHTML = `
    <div style="font-size: 16px; font-weight: 700; margin-bottom: 12px; color: #0f172a;">
      QAモード
    </div>
    <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="background: #ef4444; color: white; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 600;">
          エラー ${errors.length}
        </span>
        <span style="font-size: 13px; color: #64748b;">
          タップ領域・必須表示
        </span>
      </div>
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="background: #f59e0b; color: white; padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 600;">
          警告 ${warnings.length}
        </span>
        <span style="font-size: 13px; color: #64748b;">
          コントラスト・フォーカス
        </span>
      </div>
    </div>
    <button id="qa-refresh-btn" style="
      width: 100%;
      padding: 10px;
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      margin-bottom: 8px;
    ">
      再チェック
    </button>
    <button id="qa-close-btn" style="
      width: 100%;
      padding: 10px;
      background: white;
      color: #64748b;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    ">
      QAモード終了
    </button>
  `;
  
  document.body.appendChild(panel);
  
  // イベントリスナー
  document.getElementById('qa-refresh-btn')?.addEventListener('click', () => {
    initQAMode();
  });
  
  document.getElementById('qa-close-btn')?.addEventListener('click', () => {
    toggleQAMode(false);
  });
};

/**
 * QAモードを初期化
 */
export const initQAMode = () => {
  if (!isQAModeEnabled()) return;
  
  console.log('[QA Mode] チェック開始...');
  
  const result = runAllChecks();
  
  console.log('[QA Mode] チェック完了:', {
    total: result.total,
    errors: result.errors.length,
    warnings: result.warnings.length
  });
  
  // 問題ごとにコンソールに出力
  result.errors.forEach((issue, i) => {
    console.error(`[QA Error #${i + 1}]`, issue.message, issue.element, issue.details);
  });
  
  result.warnings.forEach((issue, i) => {
    console.warn(`[QA Warning #${i + 1}]`, issue.message, issue.element, issue.details);
  });
  
  // オーバーレイを表示
  showQAOverlay(result.issues);
  
  return result;
};

/**
 * ページ読み込み時に自動実行
 */
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    if (isQAModeEnabled()) {
      // DOMが完全に構築されるまで少し待つ
      setTimeout(() => {
        initQAMode();
      }, 1000);
    }
  });
  
  // デバッグ用にグローバルに公開
  window.medichatQA = {
    enable: () => toggleQAMode(true),
    disable: () => toggleQAMode(false),
    check: initQAMode,
    checkTapTargets,
    checkContrast,
    checkRequiredFields,
    checkFocusable
  };
}

